import type { Definition, MissionScript, OptionalView, ScriptWhen } from '../api/missions';
import { ca } from '../i18n/ca';

/**
 * A scripted recipe's script put into words for the designer's read-only
 * *Guió* panel (docs/design/ENGINE_DESIGN.md §2.20, engine step E12b): a
 * phase's trigger, what it does, where a target stands. Pure, so a phase graph
 * reads the same in a test as on the page. A key the runtime's registry knows
 * and this file does not is shown by its own name rather than dropped.
 */

const w = ca.script.when;

type Script = NonNullable<Definition['script']>;

/** Where a phase's target stands, named as the definition names it. */
export function targetWords(target: unknown, def: Definition): string {
  if (typeof target !== 'string') return w.unknown(String(target));
  const [kind, rest = ''] = target.split(/:(.*)/s);
  const t = ca.script.target;
  if (kind === 'point') {
    return rest === 'base'
      ? t.base
      : rest === 'insertion'
        ? t.insertion
        : rest === 'extraction'
          ? t.extraction
          : rest;
  }
  if (kind === 'scene') {
    const [sceneId, ...role] = rest.split('.');
    const scene = def.script?.scenes?.find((s) => s.id === sceneId);
    const label = scene?.label || sceneId;
    return role.length ? t.role(role.join('.'), label) : t.scene(label);
  }
  if (kind === 'objective') return `«${objectiveTitle(rest, def)}»`;
  if (kind === 'place') return t.place(rest);
  if (kind === 'force') {
    const force = def.forces?.find((f) => f.id === rest);
    return t.force(force?.label || rest);
  }
  if (kind === 'cast') return def.cast?.find((c) => c.id === rest)?.name ?? rest;
  return target;
}

function objectiveTitle(id: string, def: Definition): string {
  return def.objectives.find((o) => o.id === id)?.title || id;
}

function num(v: unknown): number {
  return typeof v === 'number' ? v : Number(v);
}

function bound(o: Record<string, unknown>): string {
  const parts: string[] = [];
  if (o.atLeast !== undefined) parts.push(w.atLeast(o.atLeast));
  if (o.atMost !== undefined) parts.push(w.atMost(o.atMost));
  if (o.equals !== undefined) parts.push(w.equals(o.equals));
  return parts.join(w.and);
}

/** One `when` (a trigger or a guard) in Catalan, its keys joined with «i». */
export function whenWords(when: ScriptWhen | undefined, def: Definition): string {
  if (!when || typeof when !== 'object') return '';
  const parts: string[] = [];
  for (const [key, raw] of Object.entries(when)) {
    if (key === 'label') continue;
    const v = raw as Record<string, unknown>;
    switch (key) {
      case 'start':
        parts.push(w.start);
        break;
      case 'all':
      case 'any': {
        const list = (Array.isArray(raw) ? raw : []).map((x) => whenWords(x as ScriptWhen, def));
        const joined = list.filter(Boolean).join(key === 'all' ? w.and : w.or);
        parts.push(list.length > 1 ? `(${joined})` : joined);
        break;
      }
      case 'not':
        parts.push(w.not(whenWords(raw as ScriptWhen, def)));
        break;
      case 'alarm':
        parts.push(w.alarm[String(raw)] ?? String(raw));
        break;
      case 'playersWithin':
      case 'playersAnyBeyond':
      case 'playersAllBeyond':
        parts.push(w[key](num(v?.distance), targetWords(v?.of, def)));
        break;
      case 'event': {
        if (typeof raw === 'string') {
          parts.push(w.event(raw));
        } else {
          const alarm = v?.alarm === 'quiet' ? w.eventQuiet : v?.alarm === 'up' ? w.eventLoud : '';
          parts.push(`${w.event(String(v?.name))}${alarm ? ` ${alarm}` : ''}`);
        }
        break;
      }
      case 'objective': {
        const state = String(v?.state);
        parts.push(w.objective(objectiveTitle(String(v?.id), def), w.states[state] ?? state));
        break;
      }
      case 'after': {
        const seconds = num(v?.seconds);
        if (typeof v?.phase === 'string') {
          const phase = def.script?.phases?.find((p) => p.id === v.phase);
          parts.push(w.afterPhase(seconds, phase?.title || String(v.phase)));
        } else {
          parts.push(w.afterEvent(seconds, String(v?.event)));
        }
        break;
      }
      case 'sun':
        parts.push(w.sun(num(v?.minutesToSunrise)));
        break;
      case 'count':
        parts.push(w.count(num(v?.atLeast), String(v?.event)));
        break;
      case 'state':
        parts.push(w.state(String(v?.key), bound(v)));
        break;
      case 'clock':
        parts.push(w.clockMark(String(v?.mark)));
        break;
      case 'players': {
        const alive = v?.alive as Record<string, unknown> | undefined;
        const within = v?.within as Record<string, unknown> | undefined;
        if (alive) parts.push(w.playersAlive(bound(alive)));
        if (within) parts.push(w.playersWithin(num(within.distance), targetWords(within.of, def)));
        break;
      }
      case 'unitsWithin':
        parts.push(
          w.unitsWithin(targetWords(v?.who, def), num(v?.distance), targetWords(v?.of, def)),
        );
        break;
      case 'aboard':
        parts.push(
          w.aboard(
            v?.who === 'players' || v?.who === undefined ? w.players : targetWords(v.who, def),
            targetWords(v?.vehicle, def),
          ),
        );
        break;
      case 'cargo':
        parts.push(w.cargo(targetWords(v?.of, def), targetWords(v?.at, def)));
        break;
      default:
        parts.push(w.unknown(key));
    }
  }
  return parts.join(w.and);
}

/** The radio call a key names, as the players will hear it; the key itself when the call script lacks it. */
function callText(key: string, def: Definition): string {
  const calls = (def as { radio?: { calls?: Record<string, { text?: string }> } }).radio?.calls;
  return calls?.[key]?.text || key;
}

/** What one entry of a phase's `do` list does, in Catalan. */
export function doWords(entry: Record<string, unknown>, def: Definition): string {
  const d = ca.script.does;
  const parts: string[] = [];
  for (const [key, raw] of Object.entries(entry)) {
    const v = raw as Record<string, unknown>;
    switch (key) {
      case 'delay':
      case 'if':
        break;
      case 'radio':
        parts.push(d.radio(callText(String(raw), def)));
        break;
      case 'activate':
        parts.push(d.activate(objectiveTitle(String(raw), def)));
        break;
      case 'mechanic': {
        const inst = def.script?.mechanicInstances?.find((m) => m.id === v?.id);
        const name = inst ? (ca.script.mechanicNames[inst.use] ?? inst.use) : String(v?.id);
        parts.push(d.mechanic(name, String(v?.mode)));
        break;
      }
      case 'resolve': {
        const state = String(v?.state);
        parts.push(d.resolve(objectiveTitle(String(v?.objective), def), w.states[state] ?? state));
        break;
      }
      case 'release':
        parts.push(
          d.release(
            (Array.isArray(v?.forces) ? (v.forces as string[]) : [])
              .map((id) => def.forces?.find((f) => f.id === id)?.label || id)
              .join(', '),
          ),
        );
        break;
      case 'posture':
        parts.push(
          d.posture(
            String(v?.force ?? v?.cast ?? ''),
            ca.script.postures[String(v?.to)] ?? String(v?.to),
          ),
        );
        break;
      case 'marker':
        parts.push(d.marker(String(v?.id)));
        break;
      case 'intel':
        parts.push(d.intel(String(v?.id)));
        break;
      case 'hint':
        parts.push(d.hint(String(v?.text)));
        break;
      case 'move':
        parts.push(d.move(targetWords(v?.to, def)));
        break;
      case 'action':
        parts.push(d.action(String(v?.id)));
        break;
      case 'emit':
        parts.push(d.emit(String(v?.name)));
        break;
      case 'clock':
        parts.push(d.clock(String(v?.mark)));
        break;
      default:
        parts.push(d.other(key));
    }
  }
  const delay =
    typeof entry.delay === 'number' && entry.delay > 0 ? ` (${d.delay(entry.delay)})` : '';
  const guard = entry.if ? ` — ${whenWords(entry.if as ScriptWhen, def)}` : '';
  return `${parts.join('; ')}${delay}${guard}`;
}

/** A place of the script in one line: its kind, what it is named after, its shape. */
export function placeWords(place: NonNullable<MissionScript['places']>[number]): string {
  const s = ca.script;
  const bits = [s.placeKinds[place.kind] ?? place.kind];
  if (place.settlement) bits[0] = `${bits[0]} ${s.namedAfter(spokenPlace(place.settlement))}`;
  if (place.variant) bits.push(s.variants[place.variant] ?? place.variant);
  if (typeof place.roadMetres === 'number') bits.push(s.byRoad(place.roadMetres));
  if (place.means) bits.push(s.means[place.means] ?? place.means);
  if (typeof place.radius === 'number') bits.push(s.radius(place.radius));
  return bits.join(' · ');
}

/**
 * A place of the script as the map names it: its kind in Catalan and what it
 * is named after, nothing else. The map is read at a glance, so the shape, the
 * road distance and the generator's own keys («keep · chapel · radi 25 m»,
 * «village_exit · roadCell») stay in the Guió panel's line (`placeWords`),
 * and a kind this web does not know is still a place in Catalan, never a key.
 */
export function placeLabel(place: NonNullable<MissionScript['places']>[number]): string {
  const s = ca.script;
  const kind = s.placeKinds[place.kind] ?? s.placeUnknown;
  return place.settlement ? `${kind} ${s.namedAfter(spokenPlace(place.settlement))}` : kind;
}

/**
 * A place's name as a person says it. A terrain's map labels say what a place
 * is, not what it is called — Altis has five places named "military" — and a
 * label used as a name reads «Base (military)» (memory
 * `a-map-label-is-not-a-places-name`). A bare label is said in Catalan; a
 * name that carries one as part of a proper name ("Kamensk military base")
 * is a name and stays as written. Parenthesised labels inside a longer text
 * («Base (military)») are said the same way.
 */
export function spokenPlace(name: string | undefined | null): string {
  if (!name) return '';
  const labels = ca.script.mapLabels;
  const say = (word: string) => labels[word.trim().replace(/\s+/g, ' ').toLowerCase()];
  const whole = say(name);
  if (whole) return whole;
  return name.replace(/\(([^()]*)\)/g, (all, inner: string) => {
    const said = say(inner);
    return said ? `(${said})` : all;
  });
}

/** The ids of every force a scene of the script owns: the ones whose scene, posture and post are the script's. */
export function scriptedForceIds(def: Definition): Set<string> {
  const ids = new Set<string>();
  for (const scene of def.script?.scenes ?? []) for (const id of scene.forces ?? []) ids.add(id);
  for (const f of def.forces ?? []) if (def.script && f.scene) ids.add(f.id);
  return ids;
}

/** How far past a camp's own reach an added enemy still answers its alarm by default (track E, open question 2). */
export const ALARM_REACH_EXTRA = 200;

/** What `settings.budget.maxActiveGroups` is when the definition writes none (the generator's medium). */
export const DEFAULT_MAX_ACTIVE_GROUPS = 30;

/**
 * The scenes whose alarm a force an editor adds may answer (release D,
 * D11.10): the camps, with where they stand and how far they reach.
 */
export function alarmScenes(def: Definition) {
  return (def.script?.scenes ?? []).filter(
    (s) => s.kind === 'camp' && Array.isArray(s.position) && s.position.length >= 2,
  );
}

/**
 * The alarm an enemy force dropped at (x, y) on a scripted night answers by
 * default: the nearest camp's, when it stands within the camp's radius plus
 * {@link ALARM_REACH_EXTRA} m; nothing beyond, and nothing on a night with no camp.
 */
export function defaultAlertWith(def: Definition, x: number, y: number): string | undefined {
  let best: { id: string; d: number } | undefined;
  for (const s of alarmScenes(def)) {
    const d = Math.hypot(x - s.position![0], y - s.position![1]);
    if (d <= (s.radius ?? 0) + ALARM_REACH_EXTRA && (!best || d < best.d)) best = { id: s.id, d };
  }
  return best ? `scene:${best.id}` : undefined;
}

/**
 * The groups the night keeps active, the way the platform counts them
 * against `settings.budget.maxActiveGroups` (D11.10): every force but a
 * held one, which the script releases.
 */
export function activeGroups(def: Definition): number {
  return (def.forces ?? []).filter((f) => !f.held).length;
}

/** The night's budget of active groups. */
export function maxActiveGroups(def: Definition): number {
  const budget = (def.settings?.budget ?? {}) as Record<string, number>;
  return typeof budget.maxActiveGroups === 'number'
    ? budget.maxActiveGroups
    : DEFAULT_MAX_ACTIVE_GROUPS;
}

/** A scene's name as a person reads it. */
export function sceneLabel(id: string, script: Script | undefined): string {
  return script?.scenes?.find((s) => s.id === id)?.label || id;
}

/**
 * The objective type a secondary key names (`Recipe.secondaryId`: the step's
 * type and its place in the recipe's optional list, `capture_hvt_1`).
 */
export function secondaryType(key: string): string {
  return key.replace(/_\d+$/, '');
}

/**
 * Whether the mission carries an accepted optional now: a twist by its id in
 * the definition's twists, a side mission by an objective of its type off the
 * main line. What a toggle starts from, and what it sends back with one flipped.
 */
export function optionalOn(o: OptionalView, def: Definition): boolean {
  if (o.kind === 'twist') return (def.twists ?? []).some((t) => t.id === o.key);
  const type = secondaryType(o.key);
  return def.objectives.some((x) => x.type === type && x.priority !== 'main');
}

/** The secondary keys the mission carries, with one flipped: what a regenerating toggle sends. */
export function secondariesWith(accepted: OptionalView[], def: Definition, flip: string): string[] {
  return accepted
    .filter((o) => o.kind === 'side')
    .filter((o) => (o.key === flip ? !optionalOn(o, def) : optionalOn(o, def)))
    .map((o) => o.key);
}

/**
 * The accepted twist ids the mission carries, with one flipped. A twist the
 * recipe does not accept (one added by hand before) is not sent: the
 * generator would refuse the whole request for it (`twist_not_admitted`).
 */
export function twistsWith(accepted: OptionalView[], def: Definition, flip: string): string[] {
  const keys = accepted.filter((o) => o.kind === 'twist').map((o) => o.key);
  const ids = (def.twists ?? []).map((t) => t.id).filter((id) => keys.includes(id));
  return ids.includes(flip) ? ids.filter((id) => id !== flip) : [...ids, flip];
}
